import React, {useState, useRef, useMemo, useEffect} from 'react';
import {Box} from '@mui/material';
import {useNavigate, useLocation} from 'react-router-dom';
import ArrTooltip from '../../../components/common/arr-tooltip/ArrTooltip';
import ArrIconButton from '../../../components/common/arr-icon-button/ArrIconButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../components/common/arr-menu/ArrMenu';
import './Navbar.scss';
import {ReactComponent as CaretDown} from '../../../assets/images/icon-caret-transparent.svg';
import {ReactComponent as SearchIcon} from '../../../assets/images/icon-search-transparent.svg';
import {ReactComponent as DashboardIcon} from '../../../assets/images/icon-home.svg';
import {ReactComponent as ManageIcon} from '../../../assets/images/icon-manage.svg';
import {ReactComponent as IngestionsIcon} from '../../../assets/images/icon-ingestions.svg';
import {ReactComponent as CategorizationIcon} from '../../../assets/images/icon-categorization.svg';
import {ReactComponent as DatasetsIcon} from '../../../assets/images/icon-datasets.svg';
import {ReactComponent as DocIcon} from '../../../assets/images/icon-document.svg';
import {ReactComponent as ExportsIcon} from '../../../assets/images/icon-exports.svg';
import {ReactComponent as ReportsIcon} from '../../../assets/images/icon-reports.svg';
import {ReactComponent as AiIcon} from '../../../assets/images/icon-ai-selected.svg';
import {ReactComponent as AiIconHover} from '../../../assets/images/icon-ai-hover.svg';
import {ReactComponent as AiIconDefault} from '../../../assets/images/icon-ai-default.svg';
import {useMenuData} from '../../../hooks/dashboard/useDashboard';
import {MenuData} from '../../dashboard/dashboardTypes';
import {useDispatch, useSelector} from 'react-redux';
import {type RootState} from '../../../redux/store';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {setUserRights} from '../../../redux/slice/actionMenuUerRights';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {setTabValue} from '../../../redux/slice/createCustomIndexSlice';

const iconByLabel: Record<
  string,
  React.ReactElement<React.SVGProps<SVGSVGElement>>
> = {
  Dashboard: (
    <DashboardIcon data-testid="dashboard-icon" className="icon-navbar" />
  ),
  DocExplorer: <DocIcon data-testid="doc-icon" className="icon-navbar" />,
  DataImports: (
    <DatasetsIcon data-testid="datasets-icon" className="icon-navbar" />
  ),
  Manage: <ManageIcon data-testid="manage-icon" className="icon-navbar" />,
  Search: <SearchIcon data-testid="search-icon" className="icon-navbar" />,
  Ingestions: (
    <IngestionsIcon data-testid="ingestions-icon" className="icon-navbar" />
  ),
  Reports: <ReportsIcon data-testid="reports-icon" className="icon-navbar" />,
  Exports: (
    <ExportsIcon data-testid="productions-icon" className="icon-navbar" />
  ),
  Categorize: (
    <CategorizationIcon data-testid="categorize-icon" className="icon-navbar" />
  ),
  GenerativeAI: <AiIcon data-testid="ai-icon" className="icon-navbar" />,
  AISolutions: <AiIcon data-testid="ai-icon" className="icon-navbar" />,
};

const excludedMenuItems = [
  'Instance',
  'Schedules',
  'WorkFlow',
  'Classification',
];

export enum RoleId {
  DomainAdministrator = -2,
  ProjectAdministrator = 1,
  ReviewManager = 2,
  Reviewer = 3,
  SystemAdministrator = -1,
}

export type NavigationItem = {
  id: string;
  label: string;
  icon: React.ReactElement<React.SVGProps<SVGSVGElement>>;
  href?: string;
  children?: NavigationItem[];
  disabled?: boolean;
  roles?: RoleId[];
};

export const getIconByLabel = (
  label: string
): React.ReactElement<React.SVGProps<SVGSVGElement>> =>
  iconByLabel[label] || <DashboardIcon />;

const filterEnabled = (items: MenuData[]) =>
  items.filter(
    (item) =>
      item.isEnabled &&
      item.canAccess &&
      !excludedMenuItems.includes(item.menuLabel)
  );

export const toCustomMenuItems = (items: MenuData[]): CustomMenuItem[] =>
  filterEnabled(items).map((item) => ({
    id: String(item.menuID),
    label: item.menuLabel,
    href: item.url || undefined,
    submenu: item.subMenu?.length ? toCustomMenuItems(item.subMenu) : undefined,
  }));

export const toNavigationItems = (items: MenuData[]): NavigationItem[] =>
  filterEnabled(items).map((i) => ({
    id: String(i.menuID),
    label: i.menuLabel,
    icon: getIconByLabel(i.menuLabel),
    href: i.url || undefined,
    disabled: !i.isEnabled,
    children: i.subMenu?.length ? toNavigationItems(i.subMenu) : undefined,
  }));

const removeSpacesFromMenuLabels = (items: MenuData[]): MenuData[] => {
  return items.map((item) => ({
    ...item,
    menuLabel: item.menuLabel.replace(/\s+/g, ''),
    subMenu: item.subMenu?.length
      ? removeSpacesFromMenuLabels(item.subMenu)
      : [],
  }));
};

export function useNavbarMenu() {
  const loginUser = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const {data: menuData, isLoading, error} = useMenuData(loginUser?.projectId);
  const dispatch = useDispatch();
  const processedMenuData = useMemo(() => {
    if (!menuData?.menu) return null;
    return {
      ...menuData,
      menu: removeSpacesFromMenuLabels(menuData.menu),
    };
  }, [menuData]);

  // persist processed menu into redux store
  useEffect(() => {
    if (processedMenuData?.userRights) {
      dispatch(
        setUserRights({
          ...processedMenuData.userRights,
        })
      );
    }
  }, [processedMenuData, menuData?.version, dispatch]);
  const customMenuItems = React.useMemo(
    () => (processedMenuData ? toCustomMenuItems(processedMenuData?.menu) : []),
    [processedMenuData]
  );
  const navigationItems = React.useMemo(
    () => (processedMenuData ? toNavigationItems(processedMenuData?.menu) : []),
    [processedMenuData]
  );

  return {
    customMenuItems,
    navigationItems,
    getIconByLabel,
    version: menuData?.version || '1.0.0',
    loading: isLoading,
    error,
  };
}

export type ArrNavigationSidebarProps = {
  logo?: React.ReactElement;
  className?: string;
  collapsed?: boolean;
  onItemClick?: (item: NavigationItem) => void;
};

const normalizeNavPath = (value: string): string => {
  const withoutQuery = value.split(/[?#]/)[0] || '/';
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery;
};

/** True when pathname is the menu href or a nested route under it. */
export const pathMatchesNavHref = (
  pathname: string,
  href?: string
): boolean => {
  if (!href) return false;
  const normalizedHref = normalizeNavPath(href);
  const normalizedPath = normalizeNavPath(pathname);
  if (!normalizedHref || normalizedHref === '/') {
    return normalizedPath === normalizedHref;
  }
  return (
    normalizedPath === normalizedHref ||
    normalizedPath.startsWith(`${normalizedHref}/`)
  );
};

const Navbar: React.FC<ArrNavigationSidebarProps> = ({
  logo,
  className = '',
  collapsed = false,
  onItemClick,
}) => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );
  useEffect(() => {
    if (roleId !== undefined && roleId !== null) {
      queryClient.invalidateQueries({queryKey: ['menu']});
    }
  }, [roleId, queryClient]);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const submenuRefs = useRef<Map<string, ArrMenuHandle>>(new Map());
  const submenuTriggerRefs = useRef<Map<string, HTMLElement>>(new Map());

  const {navigationItems} = useNavbarMenu();
  const {trackGAEvent} = useAnalytics();
  const hrefMapRef = useRef<Map<string, string>>(new Map());

  const getNavLabel = (label: string) => t(`verticalNavBar.${label}` as string);

  const trackNavClick = (name: string, action: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name,
        type: GA_CLICK_TYPES.ICON,
        region: GA_REGIONS.LEFT_RAIL,
        action,
      },
    });
  };

  // Use dynamic navigation items
  const filteredItems = navigationItems;

  useEffect(() => {
    hrefMapRef.current.clear();
  }, [navigationItems]);

  const handleItemAction = (
    item: NavigationItem,
    sourceEl: HTMLElement,
    event?: React.SyntheticEvent
  ) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (item.disabled) return;

    onItemClick?.(item);
    const itemLabel = getNavLabel(item.label);

    if (item.children && item.children.length > 0) {
      if (selectedItem === item.id) {
        setSelectedItem(null);

        const menuRef = submenuRefs.current.get(item.id);
        if (menuRef) {
          menuRef.close();
        }
      } else {
        setSelectedItem(item.id);
        submenuTriggerRefs.current.set(item.id, sourceEl);

        const menuRef = submenuRefs.current.get(item.id);
        menuRef?.open(sourceEl);

        trackNavClick(
          itemLabel,
          t('gaTracking.global.leftNavSubmenuOpen', {name: itemLabel})
        );
      }
    } else if (item.href) {
      trackNavClick(
        itemLabel,
        t('gaTracking.global.leftNavClick', {name: itemLabel})
      );
      navigate(item.href);
      setSelectedItem(null);
    }
  };

  const convertToMenuItems = (children: NavigationItem[]): CustomMenuItem[] => {
    return children.map((child) => {
      if (child.href) {
        hrefMapRef.current.set(child.id, child.href);
      }
      return {
        id: child.id,
        label:
          typeof child.label === 'string'
            ? t(`verticalNavBar.${child.label}` as unknown as string)
            : child.label,
      };
    });
  };

  const isItemSelected = (item: NavigationItem): boolean => {
    if (pathMatchesNavHref(location.pathname, item.href)) return true;
    if (item.children) {
      return item.children.some((child) =>
        pathMatchesNavHref(location.pathname, child.href)
      );
    }
    return false;
  };

  const handleSubmenuItemClick = (
    parentItem: NavigationItem,
    submenuItem: CustomMenuItem
  ) => {
    if (submenuItem?.label === t('verticalNavBar.AnalyticsandIndexing')) {
      dispatch(setTabValue(0));
    }

    const href = hrefMapRef.current.get(submenuItem.id);

    const parentLabel = getNavLabel(parentItem.label);

    trackNavClick(
      `${parentLabel} ${submenuItem.label}`,
      t('gaTracking.global.leftNavSubmenuClick', {
        name: submenuItem.label,
        parent: parentLabel,
      })
    );

    if (href) {
      navigate(href);
      setSelectedItem(null);
    }
  };

  const handleSubmenuClose = (itemId: string) => {
    setSelectedItem(null);
    const triggerEl = submenuTriggerRefs.current.get(itemId);
    if (triggerEl) {
      requestAnimationFrame(() => triggerEl.focus());
    }
  };

  const createSubmenuRefCallback = useMemo(() => {
    return (itemId: string) => {
      return (ref: ArrMenuHandle | null) => {
        if (ref) {
          submenuRefs.current.set(itemId, ref);
        } else {
          submenuRefs.current.delete(itemId);
        }
      };
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.closest('.MuiModal-root') ||
        target.closest('.MuiPopover-root')
      ) {
        return;
      }
      if (
        !target.closest('.navigation-sidebar') &&
        !target.closest('.arr-menu')
      ) {
        if (selectedItem) {
          const menuRef = submenuRefs.current.get(selectedItem);
          if (menuRef) {
            menuRef.close();
          }
          setSelectedItem(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [selectedItem]);

  const getCurrentSubmenuItem = (
    parentItem: NavigationItem
  ): CustomMenuItem | undefined => {
    if (!parentItem.children) return undefined;
    const currentChild = parentItem.children
      .filter((child) => pathMatchesNavHref(location.pathname, child.href))
      .sort((a, b) => (b.href?.length ?? 0) - (a.href?.length ?? 0))[0];
    if (currentChild) {
      return {
        id: currentChild.id,
        label:
          typeof currentChild.label === 'string'
            ? t(`verticalNavBar.${currentChild.label}` as unknown as string)
            : currentChild.label,
      };
    }
    return undefined;
  };

  const getNavIcon = (
    item: NavigationItem,
    isSelected: boolean,
    isHovered: boolean
  ) => {
    if (item.label === 'GenerativeAI' || item.label === 'AISolutions') {
      if (isHovered) {
        return (
          <AiIconHover data-testid="ai-icon-hover" className="icon-navbar" />
        );
      }
      if (isSelected) {
        return (
          <AiIcon data-testid="ai-icon-selected" className="icon-navbar" />
        );
      }
      return (
        <AiIconDefault data-testid="ai-icon-default" className="icon-navbar" />
      );
    }
    return item.icon;
  };

  const triggerItemAction = (
    e: React.KeyboardEvent<HTMLElement>,
    item: NavigationItem
  ) => {
    e.preventDefault();
    e.stopPropagation();
    handleItemAction(item, e.currentTarget, e);
  };

  const renderNavigationItem = (item: NavigationItem): React.ReactElement => {
    const isSelected = isItemSelected(item);
    const hasChildren = item.children && item.children.length > 0;
    const isHovered = hoveredItem === item.id;
    const isSubmenuOpen = selectedItem === item.id;
    const currentSubmenuItem = getCurrentSubmenuItem(item);

    const isSelectedOrOpen = isSelected || isSubmenuOpen;
    const itemIcon = getNavIcon(item, isSelectedOrOpen, isHovered);
    const submenuId = `nav-submenu-${item.id}`;
    const itemContent = (
      <Box
        component="button"
        type="button"
        className={`nav-item ${
          isSelected ? 'nav-item--selected' : ''
        } ${isHovered ? 'nav-item--hovered' : ''} ${
          item.disabled ? 'nav-item--disabled' : ''
        } ${collapsed ? 'nav-item--collapsed' : ''} ${
          isSubmenuOpen && !currentSubmenuItem ? 'nav-item--hovered' : ''
        }`}
        onClick={(e) => {
          handleItemAction(item, e.currentTarget, e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            triggerItemAction(e, item);
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ') {
            triggerItemAction(e, item);
          }
        }}
        onMouseEnter={() => setHoveredItem(item.id)}
        onMouseLeave={() => setHoveredItem(null)}
        aria-label={collapsed ? item.label : undefined}
        aria-current={isSelected ? 'page' : undefined}
        aria-expanded={hasChildren ? isSubmenuOpen : undefined}
        aria-controls={hasChildren ? submenuId : undefined}
        aria-haspopup={hasChildren ? 'menu' : undefined}
      >
        <ArrIconButton
          icon={itemIcon}
          onClick={() => {}}
          ariaLabel={item.label}
          size="medium"
          shape="none"
          className="nav-item__icon-button"
          testId={`navbar-icon-button-${item.label}`}
          tabIndex={-1}
        />
        {!collapsed && (
          <Box className="nav-item__content">
            <span className="nav-item__label" data-testid={`navbar-span`}>
              {item.label}
            </span>
            {hasChildren && (
              <div
                className={`caret-icon ${isSubmenuOpen ? 'caret-icon--open' : ''}`}
                data-testid={`navbar-div-collapse-${item.id}`}
              >
                <CaretDown />
              </div>
            )}
          </Box>
        )}

        {collapsed && hasChildren && (
          <div
            className={`caret-icon-collapsed ${isSubmenuOpen ? 'caret-icon-collapsed--open' : ''}`}
            data-testid={`navbar-div-expand-${item.id}`}
          >
            <CaretDown />
          </div>
        )}
      </Box>
    );

    return (
      <Box key={item.id} className="nav-item-wrapper">
        {collapsed ? (
          <ArrTooltip
            content={
              typeof item.label === 'string'
                ? t(`verticalNavBar.${item.label}` as unknown as string)
                : item.label
            }
            trigger="hover"
            placement="right"
            offset={-8}
            focusableWrapper={true}
          >
            {itemContent}
          </ArrTooltip>
        ) : (
          itemContent
        )}

        {hasChildren && (
          <ArrMenu
            ref={createSubmenuRefCallback(item.id)}
            items={convertToMenuItems(item.children!)}
            onItemClick={(submenuItem) =>
              handleSubmenuItemClick(item, submenuItem)
            }
            direction="right"
            id={submenuId}
            title={
              ['AISolutions', 'GenerativeAI'].includes(item.label)
                ? 'AI Solutions'
                : item.label
            }
            className="navbar-submenu"
            selectedCustomMenuItem={currentSubmenuItem}
            autoFocusMenu
            disableAutoFocusItem={false}
            focusFirstMenuItemOnOpen
            onClose={() => handleSubmenuClose(item.id)}
          />
        )}
      </Box>
    );
  };

  return (
    <Box
      className={`navigation-sidebar ${
        collapsed ? 'navigation-sidebar--collapsed' : ''
      } ${className || ''}`}
    >
      {logo && (
        <Box
          className={`navigation-sidebar__logo ${
            collapsed ? 'navigation-sidebar__logo--collapsed' : ''
          }`}
        >
          {logo}
        </Box>
      )}

      <Box className="navigation-sidebar__items">
        {filteredItems.map((item) => renderNavigationItem(item))}
      </Box>
    </Box>
  );
};

export default Navbar;
